import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"

import { CopyLinkField } from "./CopyLinkField"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    footer: {
        paddingTop: "1.125rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    resetButton: {
        "&.MuiButton-root": {
            color: theme.palette.tailwind.red[600],
        },
    },
}))

type Props = Readonly<{
    open: boolean
    /** Localized by the parent through `LocalizedStrict` attrs. */
    dialogTitle: string
    subtitle: string
    permalink: string
    /** Shown as a second copy field when the display can be iframed. */
    embedCode?: string
    onClose: () => void
    /** The footer's "Reset link" shortcut, which continues into the reset confirmation. */
    onResetLink: () => void
}>

/** The card's link dialog: the display's public link, plus the embed snippet when there is one. */
export function DisplayLinkDialog({ open, dialogTitle, subtitle, permalink, embedCode, onClose, onResetLink }: Props) {
    const { classes } = useStyles()

    return (
        <StandardDialog open={open} dialogTitle={dialogTitle} maxWidthProp="sm" onClose={onClose}>
            <Stack spacing="1.25rem" className={classes.content}>
                <Typography variant="body2" className={classes.subtitle}>
                    {subtitle}
                </Typography>

                <CopyLinkField
                    labelId="merchant-info-displays-link-dialog-permalink-label"
                    labelFallback="Permalink"
                    value={permalink}
                />
                {embedCode && (
                    <CopyLinkField
                        labelId="merchant-info-displays-link-dialog-embed-label"
                        labelFallback="Embed code"
                        value={embedCode}
                        isMultiline
                    />
                )}

                <Stack direction="row" alignItems="center" justifyContent="space-between" className={classes.footer}>
                    <LocalizedStrict id="merchant-info-displays-link-dialog-reset-button">
                        <StandardButton
                            variant="text"
                            fullWidth={false}
                            size="small"
                            className={classes.resetButton}
                            onClick={onResetLink}
                        >
                            Reset link
                        </StandardButton>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-info-displays-link-dialog-done-button">
                        <StandardButton
                            variant="outlined"
                            color="black"
                            fullWidth={false}
                            size="small"
                            onClick={onClose}
                        >
                            Done
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </Stack>
        </StandardDialog>
    )
}
